import { BASE_URL } from 'config';
import React, { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import DataTable from "examples/Tables/DataTable";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import IconButton from "@mui/material/IconButton";
import Swal from "sweetalert2";

function WalletsOverview() {
  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [topups, setTopups] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    // get all wallets data
    const fetchAll = async () => {
      setLoading(true);
      const token = localStorage.getItem("user-token");
      try {
        const [withdrawalsRes, transfersRes, topupsRes] = await Promise.all([
          fetch(BASE_URL + "/administrator/wallet-withdrawals", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(BASE_URL + "/administrator/wallet-transfers", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(BASE_URL + "/administrator/wallet-topups", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const withdrawalsData = await withdrawalsRes.json();
        const transfersData = await transfersRes.json();
        const topupsData = await topupsRes.json();
        setWithdrawals(withdrawalsData.data || []);
        setTransfers(transfersData.data || []);
        setTopups(topupsData.data || []);
      } catch (e) {
        setWithdrawals([]);
        setTransfers([]);
        setTopups([]);
      }
      setLoading(false);
    };
    fetchAll();
    WalletsOverview.fetchAll = fetchAll;
  }, []);

  // جداول العمليات
  const withdrawalsTable = {
    columns: [
      { Header: "رقم العملية", accessor: "id", align: "center" },
      { Header: "صاحب المحفظة", accessor: "user", align: "center" },
      { Header: "المبلغ", accessor: "amount", align: "center" },
      { Header: "العملة", accessor: "currency", align: "center" },
      { Header: "طريقة السحب", accessor: "method", align: "center" },
      { Header: "الحالة", accessor: "status", align: "center" },
      { Header: "تاريخ التنفيذ", accessor: "processed_at", align: "center" },
      { Header: "البنك", accessor: "bank", align: "center" },
    ],
    rows: withdrawals.map((w) => ({
      id: w.id,
      user: w.wallet?.user_id || "-",
      amount: w.amount,
      currency: w.wallet?.currency_id || "-",
      method: w.method === "bank_transfer" ? "تحويل بنكي" : w.method === "cash" ? "نقدي" : w.method,
      status: w.status === "processed" ? "تمت المعالجة" : w.status,
      processed_at: w.processed_at ? w.processed_at.split(" ")[0] : "-",
      bank: w.bank_account ? w.bank_account.name : "-",
    })),
  };
  const withdrawalsTotal = withdrawals.reduce((acc, w) => acc + parseFloat(w.amount || 0), 0);

  const transfersTable = {
    columns: [
      { Header: "رقم العملية", accessor: "id", align: "center" },
      { Header: "من محفظة", accessor: "from", align: "center" },
      { Header: "إلى محفظة", accessor: "to", align: "center" },
      { Header: "المبلغ", accessor: "amount", align: "center" },
      { Header: "الحالة", accessor: "status", align: "center" },
      { Header: "تاريخ التحويل", accessor: "created_at", align: "center" },
    ],
    rows: transfers.map((t) => ({
      id: t.id,
      from: t.from_wallet?.user_id || t.from_wallet_id,
      to: t.to_wallet?.user_id || t.to_wallet_id,
      amount: t.amount,
      status: t.status === "completed" ? "مكتمل" : t.status,
      created_at: t.created_at ? t.created_at.split("T")[0] : "-",
    })),
  };
  const transfersTotal = transfers.reduce((acc, t) => acc + parseFloat(t.amount || 0), 0);

  const handleApproveTopup = async (topup) => {
    const token = localStorage.getItem("user-token");
    Swal.fire({
      title: `تأكيد الموافقة على الشحن رقم ${topup.id}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "موافقة",
      cancelButtonText: "إلغاء",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/administrator/wallet-topups/completed/${topup.id}`,
            {
              method: "GET",
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const data = await res.json();
          if (!data.status) throw new Error(data.message || "فشل العملية");
          return data;
        } catch (e) {
          Swal.showValidationMessage(e.message);
          return false;
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed && result.value?.status) {
        Swal.fire({
          icon: "success",
          title: "تمت الموافقة!",
          text: "تمت الموافقة على الشحن بنجاح",
        }).then(() => {
          WalletsOverview.fetchAll && WalletsOverview.fetchAll();
        });
      }
    });
  };
  const handleRejectTopup = async (topup) => {
    const token = localStorage.getItem("user-token");
    Swal.fire({
      title: `تأكيد رفض الشحن رقم ${topup.id}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "رفض",
      cancelButtonText: "إلغاء",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/administrator/wallet-topups/cancelled/${topup.id}`,
            {
              method: "GET",
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const data = await res.json();
          if (!data.status) throw new Error(data.message || "فشل العملية");
          return data;
        } catch (e) {
          Swal.showValidationMessage(e.message);
          return false;
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed && result.value?.status) {
        Swal.fire({
          icon: "success",
          title: "تم الرفض!",
          text: "تم رفض الشحن بنجاح",
        }).then(() => {
          WalletsOverview.fetchAll && WalletsOverview.fetchAll();
        });
      }
    });
  };

  const topupsTable = {
    columns: [
      { Header: "رقم العملية", accessor: "id", align: "center" },
      { Header: "صاحب المحفظة", accessor: "user", align: "center" },
      { Header: "المبلغ", accessor: "amount", align: "center" },
      { Header: "طريقة الشحن", accessor: "method", align: "center" },
      { Header: "الحالة", accessor: "status", align: "center" },
      { Header: "تاريخ الشحن", accessor: "created_at", align: "center" },
      { Header: "الإجراءات", accessor: "actions", align: "center" },
    ],
    rows: topups.map((t) => {
      let statusLabel, statusColor;
      if (t.status === "completed") {
        statusLabel = "مكتملة";
        statusColor = "#388e3c";
      } else if (t.status === "cancelled") {
        statusLabel = "ملغاة";
        statusColor = "#d32f2f";
      } else {
        statusLabel = "قيد الانتظار";
        statusColor = "#ff9800";
      }
      return {
        id: t.id,
        user: t.wallet_id,
        amount: t.amount,
        method: t.method === "cash" ? "نقدي" : t.method,
        status: <span style={{ color: statusColor, fontWeight: 700 }}>{statusLabel}</span>,
        created_at: t.created_at ? t.created_at.split("T")[0] : "-",
        actions:
          t.status === "pending" ? (
            <MDBox display="flex" gap={1} justifyContent="center">
              <IconButton
                color="success"
                onClick={() => handleApproveTopup(t)}
                sx={{ bgcolor: "#e8f5e9", "&:hover": { bgcolor: "#b9f6ca" } }}
              >
                <CheckCircleIcon sx={{ color: "#43a047" }} />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => handleRejectTopup(t)}
                sx={{ bgcolor: "#ffebee", "&:hover": { bgcolor: "#ffcdd2" } }}
              >
                <CancelIcon sx={{ color: "#e53935" }} />
              </IconButton>
            </MDBox>
          ) : null,
      };
    }),
  };
  const topupsTotal = topups.reduce((acc, t) => acc + parseFloat(t.amount || 0), 0);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  return (
    <>
      <Backdrop open={loading} sx={{ zIndex: 2000, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <MDBox mt={4} width="100%" display="flex" flexDirection="column" alignItems="center">
        <Card
          sx={{
            width: "100%",
            maxWidth: 1100,
            minWidth: 350,
            p: 2,
            borderRadius: "16px",
            boxShadow: 2,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ mb: 3, borderRadius: "8px", background: "secondary.main" }}
          >
            <Tab label="عمليات الشحن" />
            <Tab label="عمليات التحويلات" />
            <Tab label="عمليات السحب" />
          </Tabs>
          {tabValue === 0 && (
            <>
              {/*  <MDTypography variant="h6" fontWeight="bold" mb={2} textAlign="center">
                عمليات الشحن
              </MDTypography> */}

              <div style={{ overflowY: "auto", minWidth: 400, maxHeight: 600 }}>
                <DataTable
                  table={topupsTable}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={true}
                  canSearch={true}
                  noEndBorder
                />
              </div>
              <MDBox my={2} fontWeight="bold" sx={{ color: "text.main", fontWeight: 700 }}>
                إجمالي الشحن:{" "}
                <span style={{ color: "#16a34a", fontWeight: 700 }}>{topupsTotal.toFixed(2)}</span>
              </MDBox>
            </>
          )}
          {tabValue === 1 && (
            <>


              <div style={{ overflowY: "auto", minWidth: 400, maxHeight: 600 }}>
                <DataTable
                  table={transfersTable}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={true}
                  canSearch={true}
                  noEndBorder
                />
              </div>
              <MDBox my={2} fontWeight="bold" sx={{ color: "text.main", fontWeight: 700 }}>
                إجمالي التحويلات:{" "}
                <span style={{ color: "#7b2ff2", fontWeight: 700 }}>
                  {transfersTotal.toFixed(2)}
                </span>
              </MDBox>
            </>
          )}
          {tabValue === 2 && (
            <>


              <div style={{ overflowY: "auto", minWidth: 400, maxHeight: 600 }}>
                <DataTable
                  table={withdrawalsTable}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={true}
                  canSearch={true}
                  noEndBorder
                />
              </div>
              <MDBox mb={2} fontWeight="bold" sx={{ color: "text.main", fontWeight: 700 }}>
                إجمالي السحب:{" "}
                <span style={{ color: "#ef4444", fontWeight: 700 }}>
                  {withdrawalsTotal.toFixed(2)}
                </span>
              </MDBox>
            </>
          )}
        </Card>
      </MDBox>
    </>
  );
}

export default WalletsOverview;
